import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { Autocomplete, TextField } from "@mui/material";
import Swal from "sweetalert2";
import request from "../../../Helpers/API";
import { FormatMoney } from "../../../Helpers/FormatNumber";
import { FormatNumeroInput, LimpiarNumero } from "../../../Helpers/FormatNumberInput";
import { LoaderScreen } from "../../components/LoaderScreen";

// Roles Ventas de Servicio — no eligen asesor, la cotización se registra a su propio nombre
const ROLES_VESC = ["VESC", "UCBA"];

const IVA = 0.16;

const FILA_VACIA = () => ({
  idServicio: 0,
  producto: "",
  descripcion: "",
  precioLista: "",
  cantidad: 1,
  descuento: 0,
  caracteristicas: [],
});

const importeFila = (f) => (Number(f.precioLista) || 0) * (Number(f.cantidad) || 0);
const impuestoFila = (f, aplicaIva) => (aplicaIva ? importeFila(f) * IVA : 0);
const totalFila = (f, aplicaIva) => importeFila(f) - (Number(f.descuento) || 0) + impuestoFila(f, aplicaIva);

// Placeholder de folio para alta nueva — el SP espera un folio de 13 caracteres para
// devolver los combos (Clientes/Asesores/Origenes/TiposPago) sin cotización precargada.
const FOLIO_NUEVO = "XXXXXXXXXXXXX";

// GetResponsablesContacto/responsablesContacto regresan [{ responsable: "..." }], no strings sueltos
const nombresResponsablesContacto = (lista) =>
  (Array.isArray(lista) ? lista : []).map((r) => (typeof r === "string" ? r : r?.responsable)).filter(Boolean);

// Vigencia por defecto en alta nueva: último día del mes actual
const hoy = new Date();
const ULTIMO_DIA_MES = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).toISOString().substring(0, 10);

// Alta/edición de Cotización de Servicio — captura general a la mitad superior de pantalla
// (sin modal) + tabla de artículos presupuestados debajo. Con ?folio= en la URL, precarga la
// cotización (CotizacionesCRM/ObtenerCotizacionPorFolio) para editar; sin folio, es alta nueva.
// Guarda con CotizacionesCRM/GuardarCotizaciones (folio + datos serializado en JSON + usuario).
export const CRM_AgregarCotizacionServicio = () => {
  const history = useHistory();
  const location = useLocation();
  const folio = new URLSearchParams(location.search).get("folio");

  const { roles } = useSelector((store) => store.sesion);
  const { idempleado } = useSelector((store) => store.usuario);
  const esRolVesc = roles.some((rol) => ROLES_VESC.includes(rol.idrol));

  const [info, setInfo] = useState({
    cliente: null,
    asunto: "",
    contacto: null,
    validoHasta: ULTIMO_DIA_MES,
    asesorVentas: null,
    origen: null,
    tipoPago: null,
    lineaCotizacion: null,
    folioEquip: "",
  });

  const [articulos, setArticulos] = useState([FILA_VACIA()]);
  const [ajuste, setAjuste] = useState("");
  const [aplicaIva, setAplicaIva] = useState(true);
  const [catalogoServicios, setCatalogoServicios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [busquedaCliente, setBusquedaCliente] = useState("");
  const [busquedaContacto, setBusquedaContacto] = useState("");
  const [asesores, setAsesores] = useState([]);
  const [origenes, setOrigenes] = useState([]);
  const [tiposPago, setTiposPago] = useState([]);
  const [lineasCotizacion, setLineasCotizacion] = useState([]);
  const [responsablesContacto, setResponsablesContacto] = useState([]);
  const [loader, setLoader] = useState({ mostrar: false, alerta: false, error: false, mensaje: "" });

  useEffect(() => {
    request
      .get("CotizacionesCRM/Listado")
      .then((result) => {
        const listado = Array.isArray(result) ? result : [];
        setCatalogoServicios(listado.filter((s) => s.estatus));
      })
      .catch(() => setCatalogoServicios([]));
  }, []);

  useEffect(() => {
    setLoader((prev) => ({ ...prev, mostrar: true }));
    request
      .get(`CotizacionesCRM/ObtenerCotizacionPorFolio?folio=${folio || FOLIO_NUEVO}`)
      .then((result) => {
        setClientes(Array.isArray(result?.clientes) ? result.clientes : []);
        setAsesores(Array.isArray(result?.asesores) ? result.asesores : []);
        setOrigenes(Array.isArray(result?.origenes) ? result.origenes : []);
        setTiposPago(Array.isArray(result?.tiposPago) ? result.tiposPago : []);
        setLineasCotizacion(Array.isArray(result?.lineaCotizacion) ? result.lineaCotizacion : []);
        setResponsablesContacto(nombresResponsablesContacto(result?.responsablesContacto));

        if (folio && result?.cotizacion) {
          const cot = result.cotizacion;
          const clientesLista = Array.isArray(result?.clientes) ? result.clientes : [];
          const asesoresLista = Array.isArray(result?.asesores) ? result.asesores : [];
          const origenesLista = Array.isArray(result?.origenes) ? result.origenes : [];
          const tiposPagoLista = Array.isArray(result?.tiposPago) ? result.tiposPago : [];
          const lineasLista = Array.isArray(result?.lineaCotizacion) ? result.lineaCotizacion : [];

          const clientePrecargado = clientesLista.find((c) => c.idcliente === cot.idcliente) || null;
          setBusquedaCliente(clientePrecargado?.razon_social || "");
          setBusquedaContacto(cot.nombre_contacto || "");

          setInfo({
            cliente: clientePrecargado,
            asunto: cot.asunto || "",
            contacto: cot.nombre_contacto || "",
            validoHasta: cot.vigencia ? cot.vigencia.substring(0, 10) : "",
            asesorVentas: asesoresLista.find((a) => a.idEmpleado === cot.id_asesor) || null,
            origen: origenesLista.find((o) => o.id === cot.id_origen) || null,
            tipoPago: tiposPagoLista.find((t) => t.id === cot.id_tipo_pago) || null,
            lineaCotizacion: lineasLista.find((l) => l.id === cot.linea_cotizacion) || null,
            folioEquip: cot.folio_equip || "",
          });
          setAjuste(String(cot.ajuste ?? ""));

          const detalle = Array.isArray(result?.detalle) ? result.detalle : [];
          const caracteristicasLista = Array.isArray(result?.caracteristicas) ? result.caracteristicas : [];
          setAplicaIva(detalle.some((d) => Number(d.impuesto) > 0));
          setArticulos(
            detalle.length > 0
              ? detalle.map((d, indexDetalle) => ({
                  idServicio: d.id_servicio,
                  producto: d.nombre_servicio || d.nombre || "",
                  descripcion: d.descripcion || "",
                  precioLista: String(d.precio_lista ?? ""),
                  cantidad: d.cantidad ?? 1,
                  descuento: d.descuento ?? 0,
                  caracteristicas: caracteristicasLista
                    .filter((c) => c.orden_articulo === (d.orden ?? indexDetalle + 1))
                    .sort((a, b) => (a.orden_caracteristica ?? 0) - (b.orden_caracteristica ?? 0))
                    .map((c) => c.caracteristica || ""),
                }))
              : [FILA_VACIA()]
          );
        } else if (esRolVesc) {
          const asesoresLista = Array.isArray(result?.asesores) ? result.asesores : [];
          const propio = asesoresLista.find((a) => Number(a.idEmpleado) === Number(idempleado)) || null;
          setInfo((prev) => ({ ...prev, asesorVentas: propio }));
        }

        setLoader((prev) => ({ ...prev, mostrar: false }));
      })
      .catch((error) => {
        setLoader((prev) => ({ ...prev, mostrar: false }));
        Swal.fire("COTIZACIONES DE SERVICIO", JSON.stringify(error), "error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folio]);

  const onChangeInfo = (campo, valor) => {
    setInfo((prev) => ({ ...prev, [campo]: valor }));
  };

  // Alta nueva únicamente — al elegir cliente en el buscador se refresca el listado de
  // responsables/contacto específico de ese cliente (en edición, ya viene precargado por folio).
  const cargarResponsablesContacto = (idcliente) => {
    if (folio) return;
    setBusquedaContacto("");
    onChangeInfo("contacto", "");
    request
      .get(`CotizacionesCRM/GetResponsablesContacto?idcliente=${idcliente}`)
      .then((result) => setResponsablesContacto(nombresResponsablesContacto(result)))
      .catch(() => setResponsablesContacto([]));
  };

  const onChangeAsesorVentas = (asesor) => {
    setInfo((prev) => ({ ...prev, asesorVentas: asesor }));
  };

  const onChangeArticulo = (index, campo, valor) => {
    setArticulos((prev) => prev.map((f, i) => (i === index ? { ...f, [campo]: valor } : f)));
  };

  const onChangeArticuloNumero = (index, campo, valor) => {
    const limpio = LimpiarNumero(valor);
    if (limpio !== "" && !/^-?\d*\.?\d*$/.test(limpio)) return;
    onChangeArticulo(index, campo, limpio);
  };

  const handleAgregarCaracteristica = (index) => {
    setArticulos((prev) =>
      prev.map((f, i) => (i === index ? { ...f, caracteristicas: [...f.caracteristicas, ""] } : f))
    );
  };

  const onChangeCaracteristica = (index, indexCaracteristica, valor) => {
    setArticulos((prev) =>
      prev.map((f, i) =>
        i === index
          ? { ...f, caracteristicas: f.caracteristicas.map((c, ci) => (ci === indexCaracteristica ? valor : c)) }
          : f
      )
    );
  };

  const handleEliminarCaracteristica = (index, indexCaracteristica) => {
    setArticulos((prev) =>
      prev.map((f, i) =>
        i === index
          ? { ...f, caracteristicas: f.caracteristicas.filter((_, ci) => ci !== indexCaracteristica) }
          : f
      )
    );
  };

  const onChangeProducto = (index, valor) => {
    if (valor && typeof valor === "object") {
      setArticulos((prev) =>
        prev.map((f, i) =>
          i === index
            ? { ...f, idServicio: valor.id_servicio, producto: valor.nombre_servicio, precioLista: valor.precio_lista }
            : f
        )
      );
    } else {
      setArticulos((prev) =>
        prev.map((f, i) => (i === index ? { ...f, idServicio: 0, producto: valor || "" } : f))
      );
    }
  };

  const onChangeAjuste = (valor) => {
    const limpio = LimpiarNumero(valor);
    if (limpio !== "" && !/^-?\d*\.?\d*$/.test(limpio)) return;
    setAjuste(limpio);
  };

  const handleAgregarFila = () => {
    setArticulos((prev) => [...prev, FILA_VACIA()]);
  };

  const handleEliminarFila = (index) => {
    setArticulos((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const subtotal = articulos.reduce((acc, f) => acc + importeFila(f), 0);
  const totalDescuento = articulos.reduce((acc, f) => acc + (Number(f.descuento) || 0), 0);
  const totalImpuesto = articulos.reduce((acc, f) => acc + impuestoFila(f, aplicaIva), 0);
  const totalGeneral = subtotal - totalDescuento + totalImpuesto + (Number(ajuste) || 0);

  const handleGuardar = () => {
    if (!info.cliente) {
      Swal.fire("COTIZACIONES DE SERVICIO", "Favor de seleccionar el nombre del cliente", "warning");
      return;
    }
    if (!info.contacto) {
      Swal.fire("COTIZACIONES DE SERVICIO", "Favor de seleccionar el nombre de contacto", "warning");
      return;
    }
    if (!info.validoHasta) {
      Swal.fire("COTIZACIONES DE SERVICIO", "Favor de seleccionar la vigencia (válido hasta)", "warning");
      return;
    }
    if (!info.asesorVentas) {
      Swal.fire(
        "COTIZACIONES DE SERVICIO",
        esRolVesc
          ? "No se encontró tu registro como asesor de ventas. Contacta a sistemas."
          : "Favor de seleccionar el asesor de ventas",
        "warning"
      );
      return;
    }
    if (!info.tipoPago) {
      Swal.fire("COTIZACIONES DE SERVICIO", "Favor de seleccionar el tipo de pago", "warning");
      return;
    }
    if (articulos.length === 0 || articulos.every((f) => !f.producto)) {
      Swal.fire("COTIZACIONES DE SERVICIO", "Favor de capturar al menos un artículo presupuestado", "warning");
      return;
    }

    const datos = {
      idcliente: info.cliente.idcliente,
      propietario_cotizacion: info.asesorVentas.idEmpleado,
      asunto: info.asunto,
      nombre_contacto: info.contacto,
      vigencia: `${info.validoHasta}T00:00:00`,
      asesor_venta: info.asesorVentas.idEmpleado,
      sucursal: info.asesorVentas.idSucursal,
      gerente: 0,
      origen: info.origen?.id || 0,
      tipo_pago: info.tipoPago?.id || 0,
      linea: info.lineaCotizacion?.id || null,
      folio_equip: info.folioEquip || null,
      subtotal,
      descuento: totalDescuento,
      impuesto: totalImpuesto,
      ajuste: Number(ajuste) || 0,
      total: totalGeneral,
      detalle: articulos
        .map((f, index) => ({ ...f, orden: index + 1 }))
        .filter((f) => f.producto)
        .map((f) => ({
          orden: f.orden,
          id_servicio: f.idServicio || 0,
          nombre: f.producto,
          descripcion: f.descripcion,
          precio_lista: Number(f.precioLista) || 0,
          cantidad: Number(f.cantidad) || 0,
          importe: importeFila(f),
          descuento: Number(f.descuento) || 0,
          impuesto: impuestoFila(f, aplicaIva),
          importe_total: totalFila(f, aplicaIva),
          caracteristicas: f.caracteristicas
            .filter((c) => c.trim())
            .map((c, indexCaracteristica) => ({
              orden_caracteristica: indexCaracteristica + 1,
              caracteristica: c,
            })),
        })),
    };

    const post = {
      folio: folio || "",
      datos: JSON.stringify(datos),
    };

    setLoader((prev) => ({ ...prev, mostrar: true }));
    request
      .post("CotizacionesCRM/GuardarCotizaciones", post)
      .then((result) => {
        setLoader((prev) => ({ ...prev, mostrar: false }));
        Swal.fire("COTIZACIONES DE SERVICIO", `Cotización ${result.folio} guardada con éxito`, "success").then(() => {
          history.push("/DashClientes/CotizacionesServicio");
        });
      })
      .catch((error) => {
        setLoader((prev) => ({ ...prev, mostrar: false }));
        Swal.fire("COTIZACIONES DE SERVICIO", JSON.stringify(error), "error");
      });
  };

  return (
    <div className="crm-main-screen">
      <LoaderScreen loader={loader} />
      <div className="crm-body">
        <div className="crm-page rca-screen">
          <div className="crm-module">
            <div className="crm-module-head">
              <div className="crm-head-grid">
                <div className="crm-head-left">
                  <button className="crm-btn-regresar" onClick={() => history.goBack()}>
                    <ArrowBackIcon /> Regresar
                  </button>
                </div>
                <div className="crm-head-center">
                  <p className="crm-module-subtitle">Cotizaciones de Servicio</p>
                  <h1 className="crm-module-title">{folio ? "EDITAR COTIZACIÓN DE SERVICIO" : "NUEVA COTIZACIÓN DE SERVICIO"}</h1>
                </div>
                <div className="crm-head-right" />
              </div>
              <div className="crm-head-divider">
                <div className="crm-head-divider-dot" />
              </div>
            </div>

            <div className="crm-home-wrapper crmcs-cotizacion-wrapper scroll">
              <div className="crmcs-cotizacion">

                {/* Captura general — mitad superior de pantalla, sin modal */}
                <div className="crmcs-cotizacion-header">
                  <span className="crmcs-cotizacion-header-titulo">Información general</span>
                </div>
                <div className="crmcs-cotizacion-captura">
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={clientes}
                    getOptionLabel={(o) => (typeof o === "string" ? o : o.razon_social || "")}
                    value={info.cliente || (busquedaCliente ? busquedaCliente : null)}
                    onChange={(_, v) => {
                      if (typeof v === "string") {
                        setBusquedaCliente(v);
                        onChangeInfo("cliente", null);
                      } else if (v && v.idcliente) {
                        setBusquedaCliente(v.razon_social);
                        onChangeInfo("cliente", v);
                        cargarResponsablesContacto(v.idcliente);
                      } else {
                        setBusquedaCliente("");
                        onChangeInfo("cliente", null);
                      }
                    }}
                    onInputChange={(_, inputValue, reason) => {
                      if (reason === "input") {
                        setBusquedaCliente(inputValue);
                        onChangeInfo("cliente", null);
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.idcliente}>
                        {option.razon_social}
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Nombre de cliente" />}
                    className="crmcs-campo crmcs-campo-full"
                  />
                  <TextField
                    size="small"
                    label="Asunto"
                    value={info.asunto}
                    onChange={(e) => onChangeInfo("asunto", e.target.value)}
                    className="crmcs-campo crmcs-campo-2col"
                  />
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={responsablesContacto}
                    value={info.contacto || (busquedaContacto ? busquedaContacto : null)}
                    onChange={(_, v) => {
                      const nombre = v || "";
                      setBusquedaContacto(nombre);
                      onChangeInfo("contacto", nombre);
                    }}
                    onInputChange={(_, inputValue, reason) => {
                      if (reason === "input") {
                        setBusquedaContacto(inputValue);
                        onChangeInfo("contacto", inputValue);
                      }
                    }}
                    renderInput={(params) => <TextField {...params} label="Nombre de contacto" />}
                    className="crmcs-campo"
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Válido hasta"
                    InputLabelProps={{ shrink: true }}
                    value={info.validoHasta}
                    onChange={(e) => onChangeInfo("validoHasta", e.target.value)}
                    className="crmcs-campo"
                  />
                  {!esRolVesc && (
                    <Autocomplete
                      size="small"
                      options={asesores}
                      getOptionLabel={(o) => o.empleado || ""}
                      isOptionEqualToValue={(o, v) => o.idEmpleado === v.idEmpleado}
                      value={info.asesorVentas}
                      onChange={(_, v) => onChangeAsesorVentas(v)}
                      renderInput={(params) => <TextField {...params} label="Asesor de ventas" />}
                      className="crmcs-campo"
                    />
                  )}
                  <TextField
                    size="small"
                    label="Sucursal"
                    value={info.asesorVentas?.sucursal || ""}
                    InputProps={{ readOnly: true }}
                    className="crmcs-campo"
                  />
                  <TextField
                    size="small"
                    label="Gerente"
                    value={info.asesorVentas?.gerente || ""}
                    InputProps={{ readOnly: true }}
                    className="crmcs-campo"
                  />
                  <Autocomplete
                    size="small"
                    options={origenes}
                    getOptionLabel={(o) => o.valor || ""}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={info.origen}
                    onChange={(_, v) => onChangeInfo("origen", v)}
                    renderInput={(params) => <TextField {...params} label="Origen" />}
                    className="crmcs-campo"
                  />
                  <Autocomplete
                    size="small"
                    options={tiposPago}
                    getOptionLabel={(o) => o.valor || ""}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={info.tipoPago}
                    onChange={(_, v) => onChangeInfo("tipoPago", v)}
                    renderInput={(params) => <TextField {...params} label="Tipo de pago" />}
                    className="crmcs-campo"
                  />
                  <Autocomplete
                    size="small"
                    options={lineasCotizacion}
                    getOptionLabel={(o) => o.valor || ""}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={info.lineaCotizacion}
                    onChange={(_, v) => onChangeInfo("lineaCotizacion", v)}
                    renderInput={(params) => <TextField {...params} label="Línea" />}
                    className="crmcs-campo"
                  />
                  <TextField
                    size="small"
                    label="Folio EQUIP"
                    value={info.folioEquip}
                    onChange={(e) => onChangeInfo("folioEquip", e.target.value)}
                    inputProps={{ maxLength: 20 }}
                    className="crmcs-campo"
                  />
                </div>

                {/* Tabla de artículos presupuestados */}
                <div className="crmcs-cotizacion-header">
                  <span className="crmcs-cotizacion-header-titulo">Artículos presupuestados</span>
                </div>
                <div className="crmcs-articulos-tabla-wrap">
                  <table className="crmcs-articulos-tabla">
                    <thead>
                      <tr>
                        <th>Nº Serie</th>
                        <th className="borde-izq">Nombre de Producto</th>
                        <th className="borde-izq">Precio de lista($)</th>
                        <th className="borde-izq">Cantidad</th>
                        <th className="borde-izq">Importe($)</th>
                        <th className="borde-izq">Descuento($)</th>
                        <th className="borde-izq">Total($)</th>
                        <th className="borde-izq" style={{ color: "transparent" }}>.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articulos.map((f, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td className="crmcs-celda-producto">
                            <Autocomplete
                              size="small"
                              freeSolo
                              options={catalogoServicios}
                              getOptionLabel={(o) => (typeof o === "string" ? o : o.nombre_servicio || "")}
                              value={f.producto}
                              onChange={(_, v) => onChangeProducto(index, v)}
                              onInputChange={(_, v, reason) => reason === "input" && onChangeProducto(index, v)}
                              renderInput={(params) => (
                                <TextField {...params} placeholder="Buscar producto..." />
                              )}
                              className="crmcs-campo-producto"
                            />
                            <div className="crmcs-descripcion-row">
                              <textarea
                                value={f.descripcion}
                                onChange={(e) => onChangeArticulo(index, "descripcion", e.target.value)}
                                placeholder="Descripción"
                              />
                              <button
                                type="button"
                                className="crmcs-btn-agregar-caracteristica"
                                onClick={() => handleAgregarCaracteristica(index)}
                                title="Agregar característica"
                              >
                                <AddIcon />
                              </button>
                            </div>
                            {f.caracteristicas.length > 0 && (
                              <div className="crmcs-caracteristicas-lista">
                                {f.caracteristicas.map((c, indexCaracteristica) => (
                                  <div key={indexCaracteristica} className="crmcs-caracteristica-row">
                                    <input
                                      type="text"
                                      value={c}
                                      placeholder="Característica"
                                      onChange={(e) => onChangeCaracteristica(index, indexCaracteristica, e.target.value)}
                                    />
                                    <div onClick={() => handleEliminarCaracteristica(index, indexCaracteristica)}>
                                      <DeleteIcon />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={FormatNumeroInput(f.precioLista)}
                              onChange={(e) => onChangeArticuloNumero(index, "precioLista", e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min={1}
                              value={f.cantidad}
                              onChange={(e) => onChangeArticulo(index, "cantidad", e.target.value)}
                            />
                          </td>
                          <td>
                            <input type="text" value={FormatMoney(importeFila(f))} readOnly />
                          </td>
                          <td>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={FormatNumeroInput(f.descuento)}
                              onChange={(e) => onChangeArticuloNumero(index, "descuento", e.target.value)}
                            />
                          </td>
                          <td>
                            <input type="text" value={FormatMoney(totalFila(f, aplicaIva))} readOnly />
                          </td>
                          <td className="crmcs-celda-eliminar">
                            <div onClick={() => handleEliminarFila(index)}>
                              <DeleteIcon />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="crmcs-articulos-footer">
                  <button className="crmcs-btn-agregar-fila" onClick={handleAgregarFila}>
                    <AddIcon /> Agregar fila
                  </button>

                  <div className="crmcs-totales">
                    <div className="crmcs-totales-row">
                      <span>Subtotal ($)</span>
                      <input type="text" value={FormatMoney(subtotal)} readOnly />
                    </div>
                    <div className="crmcs-totales-row">
                      <span>Descuento ($)</span>
                      <input type="text" value={FormatMoney(totalDescuento)} readOnly />
                    </div>
                    <div className="crmcs-totales-row">
                      <span>Impuesto ($)</span>
                      <button
                        type="button"
                        className={`crmcs-btn-toggle-iva${aplicaIva ? " activo" : ""}`}
                        onClick={() => setAplicaIva((prev) => !prev)}
                        title={aplicaIva ? "IVA aplicado (16%) — clic para quitar" : "IVA no aplicado — clic para aplicar"}
                      >
                        {aplicaIva ? <CloseIcon /> : <CheckIcon />}
                      </button>
                      <input type="text" value={FormatMoney(totalImpuesto)} readOnly />
                    </div>
                    <div className="crmcs-totales-row">
                      <span>Ajuste ($)</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={FormatNumeroInput(ajuste)}
                        onChange={(e) => onChangeAjuste(e.target.value)}
                      />
                    </div>
                    <div className="crmcs-totales-row crmcs-totales-general">
                      <span>Total general ($)</span>
                      <input type="text" value={FormatMoney(totalGeneral)} readOnly />
                    </div>
                  </div>
                </div>

                <div className="crmcs-cotizacion-guardar">
                  <button className="crmcs-btn-agregar" onClick={handleGuardar}>
                    <SaveIcon /> Guardar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
